import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import {
  Device,
  DeviceIconAndLocationDto,
  MapObject,
  MapObjectPolygon,
  Port,
} from '@thingslog/repositories';
import {
  Map as LeafletMap,
  MapMarker,
  LeafletMouseEvent,
  L,
  MapPolygon as LeafletMapPolygon,
  MapCircle as LeafletMapCircle,
  MapPolyline as LeafletMapPolyline,
  MapTriangleMarker as LeafletMapTriangleMarker,
} from '@thingslog/ui-components';
import { DeviceLocationTooltip } from '../../pages/dashboard2/components';
import { ClusterableDevice, DeviceCluster, clusterDevices } from './clusterDevices';
import { ClusterPopupDevice, DeviceClusterPopup } from './DeviceClusterPopup';

export interface DevicesMapRef {
  setView: (lat: number, lng: number) => void;
}

export const DevicesMap = forwardRef<DevicesMapRef, DevicesMapProps>(
  (
    {
      center,
      zoom,
      deviceMapAttributes,
      devices: devicesProps,
      objects,
      onPinClick,
      selectedDevice,
      className,
      options,
      onCenterChange,
      onZoomChange,
      locationIndicators,
      locationPaths,
      canvas = false,
      ports,
      interaction,
      clustering = true,
    }: DevicesMapProps,
    ref: React.Ref<DevicesMapRef>
  ) => {
    // #region State
    const [markers, setMarkers] = useState<MapMarker[]>([]);
    const [polygons, setPolygons] = useState<LeafletMapPolygon[]>([]);
    const [triangleMarkers, setTriangleMarkers] = useState<LeafletMapTriangleMarker[]>([]);
    const [circles, setCircles] = useState<LeafletMapCircle[]>([]);
    const [polylines, setPolylines] = useState<LeafletMapPolyline[]>([]);
    const [devices, setDevices] = useState<Map<string, Device>>(new Map());
    const [currentZoom, setCurrentZoom] = useState<number | null>(zoom ?? null);
    const mapRef = useRef<{ map: L.Map | null }>(null);
    const dragging = interaction?.dragging ?? true;
    const touchZoom = interaction?.touchZoom ?? true;
    const scrollWheelZoom = interaction?.scrollWheelZoom ?? true;
    const doubleClickZoom = interaction?.doubleClickZoom ?? true;
    const boxZoom = interaction?.boxZoom ?? true;
    const keyboard = interaction?.keyboard ?? true;
    // #endregion

    useImperativeHandle(
      ref,
      (): DevicesMapRef => ({
        setView: (lat: number, lng: number): void => {
          mapRef?.current?.map?.setView([lat, lng]);
        },
      })
    );

    // #region Effects
    useEffect(() => {
      const clusterableDevices: ClusterableDevice[] = [];
      Object.keys(deviceMapAttributes || {}).forEach((deviceNumber: string): void => {
        if (!deviceMapAttributes) return;
        const attributes = deviceMapAttributes[deviceNumber];
        if (!attributes) return;
        if (!attributes.location) return;
        clusterableDevices.push({
          deviceNumber,
          latitude: attributes.location.latitude,
          longitude: attributes.location.longitude,
        });
      });

      const clusters: DeviceCluster[] =
        clustering && currentZoom !== null
          ? clusterDevices(clusterableDevices, currentZoom)
          : clusterableDevices.map((clusterable: ClusterableDevice) => ({
              deviceNumbers: [clusterable.deviceNumber],
              latitude: clusterable.latitude,
              longitude: clusterable.longitude,
            }));

      setMarkers(
        clusters.map((cluster: DeviceCluster) =>
          cluster.deviceNumbers.length === 1
            ? createDeviceMarker(cluster.deviceNumbers[0])
            : createClusterMarker(cluster)
        )
      );
    }, [deviceMapAttributes, devices, selectedDevice, currentZoom, clustering]);

    useEffect(() => {
      if (objects) {
        objects.forEach((mapObject: MapObject) => {
          if (mapObject && mapObject['@type'] === 'polygon') {
            const { coordinates } = mapObject as MapObjectPolygon;
            polygons.push({
              color: '#17576E',
              opacity: 0.9,
              fillOpacity: 0.05,
              positions: coordinates.map((coordinates: { x: number; y: number }) => ({
                lng: coordinates.x,
                lat: coordinates.y,
              })),
            });
          }
        });
      }
      setPolygons(polygons);
    }, [objects]);

    useEffect(() => {
      const markers: LeafletMapTriangleMarker[] = [];
      if (locationIndicators && locationIndicators.length > 1) {
        locationIndicators?.forEach(
          ({ color, location, showTooltip, deviceNumber, timestamp }: MapLocationIndicator) => {
            markers.push({
              color: color,
              opacity: 1,
              rotation: 0,
              position: [location.lat, location.lng],
              popupProps: showTooltip
                ? {
                    autoPanPaddingTopLeft: [0, 180],
                    children: (
                      <DeviceLocationTooltip
                        deviceNumber={deviceNumber}
                        timestamp={timestamp}
                        ports={ports}
                        location={location}
                      />
                    ),
                  }
                : undefined,
            });
          }
        );
      }
      setTriangleMarkers(
        markers.map(
          (marker: LeafletMapTriangleMarker, index: number, arr: LeafletMapTriangleMarker[]) => {
            const isLast = index === arr.length - 1;
            if (isLast) {
              return { ...marker, rotation: arr[index - 1].rotation };
            } else {
              const nextLocation = arr[index + 1].position;
              const currentLocation = marker.position;
              const rotation = Math.atan2(
                nextLocation[1] - currentLocation[1],
                nextLocation[0] - currentLocation[0]
              );
              return { ...marker, rotation: (rotation * 180) / Math.PI };
            }
          }
        )
      );
    }, [locationIndicators]);

    useEffect(() => {
      const polylines: LeafletMapPolyline[] = [];
      locationPaths?.forEach(({ path, color }: MapLocationPath) => {
        if (path.length > 1) {
          polylines.push({
            color: color,
            positions: path.map((position: { lat: number; lng: number }) => ({
              lat: position.lat,
              lng: position.lng,
            })),
          });
        }
      });
      setPolylines(polylines);
    }, [locationPaths]);

    useEffect(() => {
      const devicesMap = new Map<string, Device>();
      devicesProps?.forEach((device: Device) => {
        devicesMap.set(device.number, device);
      });
      setDevices(devicesMap);
    }, [devicesProps]);

    useEffect(() => {
      if (selectedDevice === null || selectedDevice === undefined) return;
      if (!deviceMapAttributes) return;
      if (options?.centerMapOnSelectedDevice === false) return;

      const attributes = deviceMapAttributes[selectedDevice.number];
      if (!attributes) return;
      const location = attributes.location;
      if (!location) return;
      mapRef?.current?.map?.setView([location.latitude, location.longitude]);
    }, [devices, selectedDevice, deviceMapAttributes]);

    useEffect(() => {
      if (mapRef.current && mapRef.current.map) {
        const initialZoom = mapRef.current.map.getZoom();
        if (initialZoom !== undefined && initialZoom !== null) {
          setCurrentZoom(initialZoom);
        }
        mapRef.current.map.addEventListener('zoomend', (event: L.LeafletEvent) => {
          const zoomLevel = event.target?.getZoom();
          if (zoomLevel !== undefined && zoomLevel !== null) {
            setCurrentZoom(zoomLevel);
            if (onZoomChange) {
              onZoomChange(zoomLevel);
            }
          }
        });
        mapRef.current.map.addEventListener('moveend', (event: L.LeafletEvent) => {
          const center = event.target?.getCenter();
          const lat = center?.lat;
          const lng = center?.lng;
          if (
            onCenterChange &&
            lat !== undefined &&
            lat !== null &&
            lng !== undefined &&
            lng !== null
          ) {
            onCenterChange(lat, lng);
          }
        });
      }
    }, [mapRef.current]);

    useEffect(() => {
      const map = mapRef.current?.map;
      if (!map) return;

      if (dragging) map.dragging.enable();
      else map.dragging.disable();

      if (touchZoom) map.touchZoom.enable();
      else map.touchZoom.disable();

      if (scrollWheelZoom) map.scrollWheelZoom.enable();
      else map.scrollWheelZoom.disable();

      if (doubleClickZoom) map.doubleClickZoom.enable();
      else map.doubleClickZoom.disable();

      if (boxZoom) map.boxZoom.enable();
      else map.boxZoom.disable();

      if (keyboard) map.keyboard.enable();
      else map.keyboard.disable();
    }, [dragging, touchZoom, scrollWheelZoom, doubleClickZoom, boxZoom, keyboard]);
    // #endregion

    // #region Functions
    const createDeviceMarker = (deviceNumber: string): MapMarker => {
      const attributes = deviceMapAttributes![deviceNumber]!;
      const device = devices.has(deviceNumber) ? devices.get(deviceNumber)! : null;

      return {
        markerProps: {
          position: [attributes.location.latitude, attributes.location.longitude],
          icon: createPinIcon({
            color: attributes.iconColors.backgroundColor,
            base64PNG: attributes.iconColors.icon,
            isSelected: selectedDevice?.number === deviceNumber,
            hasWarning: attributes.hasWarning,
          }),
          eventHandlers: {
            mouseover: (e: LeafletMouseEvent): void => {
              e.target.openPopup();
            },
            mouseout: (e: LeafletMouseEvent): void => {
              e.target.closePopup();
            },
            click: (): void => {
              if (onPinClick) {
                if (selectedDevice && device) {
                  onPinClick(selectedDevice.number === device.number ? null : device);
                } else {
                  onPinClick(device);
                }
              }
            },
          },
        },
        popupProps:
          device && device.name
            ? {
                minWidth: 80,
                maxWidth: 150,
                offset: [0, -30],
                children: (
                  <div className="break-all text-center text-sm font-semibold text-gray-700">
                    {device.name}
                  </div>
                ),
              }
            : undefined,
      };
    };

    const createClusterMarker = (cluster: DeviceCluster): MapMarker => {
      const selectedNumber =
        selectedDevice && cluster.deviceNumbers.includes(selectedDevice.number)
          ? selectedDevice.number
          : null;
      const selectedAttributes = selectedNumber ? deviceMapAttributes?.[selectedNumber] : null;
      const count = cluster.deviceNumbers.length;

      const popupDevices: ClusterPopupDevice[] = cluster.deviceNumbers.map(
        (deviceNumber: string): ClusterPopupDevice => {
          const attributes = deviceMapAttributes![deviceNumber]!;
          const device = devices.has(deviceNumber) ? devices.get(deviceNumber)! : null;
          return {
            deviceNumber,
            name: device?.name ?? null,
            color: attributes.iconColors.backgroundColor,
            icon: attributes.iconColors.icon,
            hasWarning: attributes.hasWarning,
          };
        }
      );

      const colorCounts = new Map<string, number>();
      popupDevices.forEach((popupDevice: ClusterPopupDevice) => {
        colorCounts.set(popupDevice.color, (colorCounts.get(popupDevice.color) ?? 0) + 1);
      });
      const colorShares = Array.from(colorCounts.entries())
        .sort((a: [string, number], b: [string, number]) => b[1] - a[1])
        .map(([color, colorCount]: [string, number]) => ({
          color,
          share: colorCount / count,
        }));

      const icon = selectedAttributes
        ? createPinIcon({
            color: selectedAttributes.iconColors.backgroundColor,
            base64PNG: selectedAttributes.iconColors.icon,
            isSelected: true,
            hasWarning: selectedAttributes.hasWarning,
            clusterCount: count,
          })
        : createClusterIcon(count, colorShares);

      return {
        markerProps: {
          position: [cluster.latitude, cluster.longitude],
          icon,
        },
        popupProps: {
          minWidth: 240,
          maxWidth: 280,
          offset: selectedAttributes ? [0, -36] : [0, -20],
          children: (
            <DeviceClusterPopup
              devices={popupDevices}
              selectedDeviceNumber={selectedDevice?.number}
              onDeviceClick={handleClusterDeviceClick}
            />
          ),
        },
      };
    };

    const handleClusterDeviceClick = (deviceNumber: string): void => {
      if (!onPinClick) return;
      const device = devices.has(deviceNumber) ? devices.get(deviceNumber)! : null;
      if (selectedDevice && device) {
        onPinClick(selectedDevice.number === device.number ? null : device);
      } else {
        onPinClick(device);
      }
    };

    const createClusterIcon = (
      count: number,
      colorShares: { color: string; share: number }[]
    ): L.Icon => {
      const label = count > 99 ? '99+' : String(count);
      const ringRadius = 39;
      const ringCircumference = 2 * Math.PI * ringRadius;
      let ringOffset = 0;
      const ringSegments = colorShares
        .map(({ color, share }: { color: string; share: number }): string => {
          const segmentLength = share * ringCircumference;
          const segment = `<circle cx="50" cy="50" r="${ringRadius}" fill="none" stroke="${color}" stroke-width="10" stroke-dasharray="${segmentLength} ${
            ringCircumference - segmentLength
          }" stroke-dashoffset="${-ringOffset}" transform="rotate(-90 50 50)"/>`;
          ringOffset += segmentLength;
          return segment;
        })
        .join('');
      const svg = `
    <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx="50" cy="50" r="45" fill="white"/>
      ${ringSegments}
      <circle cx="50" cy="50" r="33" fill="#37474F" stroke="white" stroke-width="2"/>
      <text x="50" y="50" text-anchor="middle" dominant-baseline="central" font-family="Arial" font-size="${
        label.length > 2 ? 26 : 32
      }" font-weight="bold" fill="white">${label}</text>
    </svg>`;
      return new L.Icon({
        iconUrl: 'data:image/svg+xml;base64,' + btoa(svg),
        iconSize: [44, 44],
        iconAnchor: [22, 22],
      });
    };

    const createPinIcon = ({
      color,
      base64PNG,
      isSelected = false,
      iconSize = [45, 45],
      iconAnchor = [25, 40],
      hasWarning = false,
      clusterCount,
    }: IconCreationOptions): L.Icon => {
      const warningBadge = hasWarning
        ? `
        <circle cx="318" cy="98" r="98" fill="red" />
        <text x="318" y="98" text-anchor="middle" dominant-baseline="central" font-family="Arial" font-size="140" font-weight="bold" fill="white">!</text>
      `
        : '';

      const clusterCountLabel = clusterCount && clusterCount > 99 ? '99+' : String(clusterCount);
      const clusterBadge = clusterCount
        ? `
        <circle cx="66" cy="98" r="98" fill="#37474F" stroke="white" stroke-width="12" />
        <text x="66" y="98" text-anchor="middle" dominant-baseline="central" font-family="Arial" font-size="${
          clusterCountLabel.length > 2 ? 100 : 120
        }" font-weight="bold" fill="white">${clusterCountLabel}</text>
      `
        : '';

      // Using a string, not renderToStaticMarkup, for performance reasons.
      const svg = `
    <svg width="100" height="100" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <path fill="${color}" d="M384 192c0 87.4-117 243-168.3 307.2c-12.3 15.3-35.1 15.3-47.4 0C117 435 0 279.4 0 192C0 86 86 0 192 0S384 86 384 192z"/>
      <image x="48" y="62" width="288" height="288" xlink:href="${base64PNG}"/>
      ${warningBadge}
      ${clusterBadge}
    </svg>`;

      const sizeMultiplier = isSelected ? 1.2 : 1;
      const scaledIconSize: [number, number] = [
        Math.round(iconSize[0] * sizeMultiplier),
        Math.round(iconSize[1] * sizeMultiplier),
      ];
      const scaledIconAnchor: [number, number] = [
        Math.round(iconAnchor[0] * sizeMultiplier),
        Math.round(iconAnchor[1] * sizeMultiplier),
      ];
      const iconUrl = 'data:image/svg+xml;base64,' + btoa(svg);
      return new L.Icon({
        iconUrl,
        iconSize: scaledIconSize,
        iconAnchor: scaledIconAnchor,
        className: isSelected ? 'animate-pulse' : undefined,
      });
    };

    // #endregion

    return (
      <LeafletMap
        ref={mapRef}
        preferCanvas={canvas}
        className={className}
        center={center}
        zoom={zoom}
        markers={markers}
        polygons={polygons}
        circles={circles}
        polylines={polylines}
        triangleMarkers={triangleMarkers}
        zoomControl={false}
        dragging={dragging}
        touchZoom={touchZoom}
        scrollWheelZoom={scrollWheelZoom}
        doubleClickZoom={doubleClickZoom}
        boxZoom={boxZoom}
        keyboard={keyboard}
      />
    );
  }
);

interface IconCreationOptions {
  color: string;
  base64PNG: string;
  isSelected?: boolean;
  iconSize?: [number, number];
  iconAnchor?: [number, number];
  hasWarning?: boolean;
  clusterCount?: number;
}

interface DevicesMapProps {
  center?: [number, number];
  ports?: Record<number, Port>;
  zoom?: number;
  deviceMapAttributes?: DeviceIconAndLocationDto;
  devices?: Device[];
  objects?: MapObject[];
  onPinClick?: (device: Device | null) => void;
  selectedDevice?: Device | null;
  className?: string;
  onZoomChange?: (zoom: number) => void;
  onCenterChange?: (lat: number, lng: number) => void;
  locationIndicators?: MapLocationIndicator[];
  locationPaths?: MapLocationPath[];
  canvas?: boolean;
  clustering?: boolean;
  interaction?: {
    dragging?: boolean;
    touchZoom?: boolean;
    scrollWheelZoom?: boolean;
    doubleClickZoom?: boolean;
    boxZoom?: boolean;
    keyboard?: boolean;
  };
  options?: {
    centerMapOnSelectedDevice?: boolean;
  };
}

export interface MapLocationPath {
  path: Location[];
  color: string;
}

export interface MapLocationIndicator {
  deviceNumber: string;
  timestamp: string;
  location: Location;
  color: string;
  showTooltip?: boolean;
}

interface Location {
  lat: number;
  lng: number;
}
